import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map } from 'rxjs';
import * as d3 from 'd3';
import { PortfolioResult } from 'src/app/pages/_components/index-calculator-v2026/api-response.model';

interface VintagePoint {
  label: string;          // '2080' … '2020', 'Income'
  vintage: number;        // 2080 … 2020, 1933
  equity: number;         // stock % (bond % = 100 − equity)
}

interface GuideCol {
  targetDate: string;
  age: string;
  years: string;
  mix: string;
}

@Component({
  selector: 'app-glide-path-chart',
  templateUrl: './glide-path-chart.component.html',
  styleUrls: ['./glide-path-chart.component.scss'],
  imports: [CommonModule]
})
export class GlidePathChartComponent implements OnInit {

  loading = signal(true);
  error = signal<string | null>(null);

  /** Stack order bottom → top. */
  private readonly series = [
    { key: 'stocks', label: '% Stock Funds', color: '#2d7daf' },
    { key: 'bonds',  label: '% Bond Funds',  color: '#76bc43' },
  ];

  private readonly TITLE_BLUE = '#1f4e79';
  private readonly ORANGE = '#e8531f';
  private readonly LANDING_GREEN = '#2d5c33';
  private readonly YOU_AMBER = '#b58500';
  private readonly YOU_CELL_BG = '#f6e7bd';
  private readonly FONT = "'Segoe UI', Arial, Helvetica, sans-serif";

  /** User options */
  userAge = signal<number | null>(null);
  retireAge = signal(65);
  hiddenVintages = signal<ReadonlySet<number>>(new Set<number>([2080, 2020]));

  private readonly DISCLOSURE =
    'An investment in a glidepath portfolio does not eliminate the need for investors to decide—before investing ' +
    'and periodically thereafter—whether the portfolio fits their financial situation. Diversification does not ' +
    'eliminate the risk of market loss. There is no guarantee investment strategies will be successful. Assumptions ' +
    'used by IFC in developing the funds’ asset allocation glide path may not be in line with future capital market ' +
    'returns and participant savings activities, which could result in losses near, at, or after the target date year. ' +
    'The actual asset allocations utilized by each fund may deviate from the allocations illustrated by this glide path.';

  @ViewChild('chart', { static: true }) private chartEl!: ElementRef<HTMLElement>;

  // 1933 = Retirement Income vintage; risk-based portfolios intentionally excluded
  private readonly TD_VINTAGES = [2080, 2075, 2070, 2065, 2060, 2055, 2050, 2045, 2040, 2035, 2030, 2025, 2020, 1933];
  private points: VintagePoint[] = [];
  private view: VintagePoint[] = [];      // points currently visible (toggles applied)
  private youCol: number | null = null;   // view index of the vintage matching the user's ages
  private asOfYear = new Date().getFullYear();

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.load();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.points.length) { this.renderChart(); }
  }

  // ── Options API (template) ─────────────────────────────────────────────────

  vintageOptions(): Array<{ label: string; vintage: number; visible: boolean }> {
    const hidden = this.hiddenVintages();
    return this.points.map(p => ({ label: p.label, vintage: p.vintage, visible: !hidden.has(p.vintage) }));
  }

  toggleVintage(vintage: number): void {
    const next = new Set(this.hiddenVintages());
    if (next.has(vintage)) {
      next.delete(vintage);
    } else {
      if (this.points.length - next.size <= 2) { return; } // keep at least 2 visible
      next.add(vintage);
    }
    this.hiddenVintages.set(next);
    this.renderChart();
  }

  onUserAge(event: Event): void {
    // No clamping mid-typing — rewriting the input while the user types eats keystrokes.
    const n = parseInt((event.target as HTMLInputElement).value, 10);
    this.userAge.set(Number.isFinite(n) && n >= 10 && n <= 100 ? n : null);
    this.renderChart();
  }

  onRetireAge(event: Event): void {
    const n = parseInt((event.target as HTMLInputElement).value, 10);
    this.retireAge.set(Number.isFinite(n) ? Math.min(Math.max(n, 40), 85) : 65);
    this.renderChart();
  }

  targetYear(): number | null {
    const a = this.userAge();
    return a === null ? null : this.asOfYear + this.retireAge() - a;
  }

  private visiblePoints(): VintagePoint[] {
    const hidden = this.hiddenVintages();
    return this.points.filter(p => !hidden.has(p.vintage));
  }

  /**
   * View index of the vintage that suits the user's age + retirement age.
   * A vintage V covers planned retirement years (V−4 .. V]; the Retirement
   * Portfolio covers anything earlier than the oldest dated vintage's band.
   */
  private computeYouCol(pts: VintagePoint[]): number | null {
    const target = this.targetYear();
    if (target === null) { return null; }
    const dated = pts.map((p, i) => ({ p, i })).filter(x => x.p.vintage !== 1933);
    if (!dated.length) { return null; }
    const minV = Math.min(...dated.map(x => x.p.vintage));
    const incomeIdx = pts.findIndex(p => p.vintage === 1933);
    if (incomeIdx >= 0 && target < minV - 4) { return incomeIdx; }
    const covering = dated.filter(x => x.p.vintage >= target);
    if (!covering.length) { return dated[0].i; } // younger than the newest vintage's band → first column
    return covering.reduce((best, x) => x.p.vintage < best.p.vintage ? x : best).i;
  }

  private fail(): void {
    this.loading.set(false);
    this.error.set('Glide path data is temporarily unavailable.');
  }

  private load(): void {
    const DATE_RANGE_URL = 'https://data2.ifa.com/api/v1/investment/performance/monthly/grr/?startDates=start&endDates=end&ids=646';
    this.http.get<{ investments: Array<{ periods: Array<{ subtitle: { start: string; end: string } }> }> }>(DATE_RANGE_URL).subscribe({
      next: dateRange => {
        const end = dateRange?.investments?.[0]?.periods?.[0]?.subtitle?.end; // 'M/D/YYYY'
        if (!end) { this.fail(); return; }
        const [m, , y] = end.split('/').map(Number);
        this.asOfYear = y;
        this.fetchEquities(new Date(y, m, 0));
      },
      error: () => this.fail()
    });
  }

  /** One 1-month glide path call per vintage to learn each vintage's current equity %. */
  private fetchEquities(endDate: Date): void {
    const baseUrl  = 'https://data2.ifa.com/odata/v1/returns/monthly/domain';
    const endStr   = this.fmt(endDate);
    const startStr = `${endDate.getMonth() + 1}/1/${endDate.getFullYear()}`;
    const calls: Observable<number | null>[] = this.TD_VINTAGES.map(vintage => {
      const params = new HttpParams()
        .set('domainStart',     `7/1/${vintage - 84}`)
        .set('domainEnd',       endStr)
        .set('startDate',       startStr)
        .set('endDate',         endStr)
        .set('portfolios',      'port92srwf_ifa')
        .set('glidePath',       'true')
        .set('dollar',          '100000')
        .set('customGlidePath', 'SRWFTargetDate')
        .set('showReturns',     'true');
      return this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`).pipe(
        map(res => {
          const monthly = res?.[0]?.periods?.[0]?.monthly;
          const sym = monthly?.[monthly.length - 1]?.symbol ?? '';
          const match = /^port(\d+)srwf_ifa$/.exec(sym);
          return match ? parseInt(match[1], 10) : null;
        })
      );
    });

    forkJoin(calls).subscribe({
      next: equities => {
        if (equities.some(e => e === null)) { this.fail(); return; }
        this.points = this.TD_VINTAGES.map((vintage, i) => ({
          label: vintage === 1933 ? 'Income' : String(vintage),
          vintage,
          equity: equities[i] as number,
        }));
        this.loading.set(false);
        // Let the @if remove the loading state before measuring the host width
        setTimeout(() => this.renderChart());
      },
      error: () => this.fail()
    });
  }

  /**
   * Each vintage owns a column. The glide path is drawn through the column
   * boundaries: the boundary between column i−1 and column i sits at vintage i's
   * equity, so a column runs from its own equity down to the next vintage's.
   * The first and last columns are flat plateaus.
   */
  private leftEquity(i: number): number {
    return this.view[i].equity;
  }

  private rightEquity(i: number): number {
    return this.view[Math.min(i + 1, this.view.length - 1)].equity;
  }

  private renderChart(): void {
    const host = this.chartEl.nativeElement;
    host.innerHTML = '';
    const pts = this.view = this.visiblePoints();
    const n = pts.length;
    if (!n) { return; }
    this.youCol = this.computeYouCol(pts);

    const width  = Math.max(host.clientWidth || 1100, 980);
    const margin = { top: 122, right: 26, left: 132 };
    const plotH      = 356;
    const plotTop    = margin.top;
    const plotBottom = plotTop + plotH;
    const plotLeft   = margin.left;
    const plotRight  = width - margin.right;
    const bandW      = (plotRight - plotLeft) / n;
    const bandX      = (i: number) => plotLeft + i * bandW;   // left edge of column i
    const [stocks, bonds] = this.series;

    const svg = d3.select(host).append('svg')
      .attr('width', '100%')
      .attr('font-family', this.FONT)
      .attr('role', 'img')
      .attr('aria-label', 'Glide path guide of IFC target date index portfolios: stock and bond fund allocations by target date');

    const y = d3.scaleLinear().domain([0, 100]).range([plotBottom, plotTop]);

    // ── Title ────────────────────────────────────────────────────────────────
    const title = svg.append('text')
      .attr('x', 24).attr('y', 44)
      .attr('font-size', 26).attr('font-weight', 700)
      .attr('fill', this.TITLE_BLUE)
      .text('Glide Path Guide of IFC Target Date Index Portfolios');
    const titleW = (title.node() as SVGTextElement).getComputedTextLength();
    svg.append('text')
      .attr('x', 24 + titleW + 14).attr('y', 44)
      .attr('font-size', 15).attr('fill', '#555')
      .text(`As of ${this.asOfYear}`);

    // ── Legend + callout pills row ───────────────────────────────────────────
    const pillY = 62, pillH = 30;

    const pill = (label: string, fill: string, rightX: number) => {
      const g = svg.append('g');
      const t = g.append('text')
        .attr('x', rightX - 13).attr('y', pillY + 20)
        .attr('text-anchor', 'end')
        .attr('font-size', 15).attr('fill', '#fff')
        .text(label);
      const w = (t.node() as SVGTextElement).getComputedTextLength() + 26;
      g.insert('rect', 'text')
        .attr('x', rightX - w).attr('y', pillY)
        .attr('width', w).attr('height', pillH).attr('rx', 3)
        .attr('fill', fill);
      return { left: rightX - w, right: rightX };
    };

    // Legend, boxed like the printed guide
    const legend = svg.append('g');
    const legendPadX = 16, swatch = 18, swatchGap = 10, itemGap = 30;
    let lx = legendPadX;
    for (const c of this.series) {
      const item = legend.append('g');
      const t = item.append('text')
        .attr('x', lx + swatch + swatchGap).attr('y', pillY + 21)
        .attr('font-size', 17).attr('fill', '#222')
        .text(c.label);
      item.insert('rect', 'text')
        .attr('x', lx).attr('y', pillY + 6)
        .attr('width', swatch).attr('height', swatch).attr('rx', 2)
        .attr('fill', c.color);
      lx += swatch + swatchGap + (t.node() as SVGTextElement).getComputedTextLength() + itemGap;
    }
    const legendW = lx - itemGap + legendPadX;
    legend.insert('rect', ':first-child')
      .attr('x', 0).attr('y', pillY - 4)
      .attr('width', legendW).attr('height', pillH + 12).attr('rx', 3)
      .attr('fill', '#fff').attr('stroke', '#7a7a7a').attr('stroke-width', 1.5);
    legend.attr('transform', `translate(${plotLeft},0)`);

    // ── Bands: stocks below the glide path, bonds above ──────────────────────
    const verts: Array<{ x: number; e: number }> = [{ x: plotLeft, e: pts[0].equity }];
    for (let i = 1; i < n; i++) { verts.push({ x: bandX(i), e: pts[i].equity }); }
    verts.push({ x: plotRight, e: pts[n - 1].equity });

    const band = (lo: (d: { e: number }) => number, hi: (d: { e: number }) => number) =>
      d3.area<{ x: number; e: number }>()
        .x(d => d.x)
        .y0(d => y(lo(d)))
        .y1(d => y(hi(d)))
        .curve(d3.curveMonotoneX);

    svg.append('path').datum(verts)
      .attr('d', band(() => 0, d => d.e))
      .attr('fill', stocks.color);
    svg.append('path').datum(verts)
      .attr('d', band(d => d.e, () => 100))
      .attr('fill', bonds.color);

    // Dotted white year gridlines inside each column
    const yearXs: number[] = [];
    for (let i = 0; i < n; i++) {
      for (let k = 1; k <= 4; k++) { yearXs.push(bandX(i) + bandW * (k / 5)); }
    }
    svg.append('g').selectAll('line')
      .data(yearXs)
      .join('line')
      .attr('x1', d => d).attr('x2', d => d)
      .attr('y1', plotTop).attr('y2', plotBottom)
      .attr('stroke', '#fff').attr('stroke-opacity', 0.75)
      .attr('stroke-width', 1.25)
      .attr('stroke-dasharray', '2 4');

    // Solid white column separators
    svg.append('g').selectAll('line')
      .data(d3.range(1, n))
      .join('line')
      .attr('x1', i => bandX(i)).attr('x2', i => bandX(i))
      .attr('y1', plotTop).attr('y2', plotBottom)
      .attr('stroke', '#fff').attr('stroke-width', 2.5);

    // Retirement line layer: above the bands and gridlines, below the glide path and its markers
    const retireLayer = svg.append('g');

    // Glide path itself + boundary markers
    svg.append('path').datum(verts)
      .attr('d', d3.line<{ x: number; e: number }>().x(d => d.x).y(d => y(d.e)).curve(d3.curveMonotoneX))
      .attr('fill', 'none').attr('stroke', '#fff').attr('stroke-width', 2.5);
    svg.append('g').selectAll('circle')
      .data(verts.slice(1, -1))
      .join('circle')
      .attr('cx', d => d.x).attr('cy', d => y(d.e))
      .attr('r', 6)
      .attr('fill', '#fff');

    // "You" marker: outline + tag on the vintage matching the user's ages
    if (this.youCol !== null) {
      const yi = this.youCol;
      svg.append('rect')
        .attr('x', bandX(yi) + 1.5).attr('y', plotTop + 1.5)
        .attr('width', bandW - 3).attr('height', plotH - 3)
        .attr('fill', '#ffd75e').attr('fill-opacity', 0.15)
        .attr('stroke', this.YOU_AMBER).attr('stroke-width', 2.5)
        .attr('stroke-dasharray', '6 4')
        .style('pointer-events', 'none');
      const tag = svg.append('g');
      const tagText = tag.append('text')
        .attr('x', bandX(yi) + bandW / 2).attr('y', plotTop + 21)
        .attr('text-anchor', 'middle')
        .attr('font-size', 13).attr('font-weight', 700).attr('fill', '#fff')
        .text('You');
      const tagW = (tagText.node() as SVGTextElement).getComputedTextLength() + 18;
      tag.insert('rect', 'text')
        .attr('x', bandX(yi) + bandW / 2 - tagW / 2).attr('y', plotTop + 6)
        .attr('width', tagW).attr('height', 21).attr('rx', 10.5)
        .attr('fill', this.YOU_AMBER);
    }

    // ── Y axis: ticks + labels only, no domain line ──────────────────────────
    svg.append('g')
      .attr('transform', `translate(${plotLeft},0)`)
      .call(d3.axisLeft(y).tickValues(d3.range(0, 101, 10)).tickSize(5).tickFormat(d => `${d}%`))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('line').attr('stroke', '#555'))
      .call(g => g.selectAll('text').attr('font-size', 12).attr('fill', '#222'));
    svg.append('text')
      .attr('transform', `translate(${plotLeft - 58},${(plotTop + plotBottom) / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .attr('font-size', 15).attr('fill', '#222')
      .text('Allocation');

    // ── Landing point: pill + bracket over the retirement-portfolio column ───
    const landing = pill('Landing Point', this.LANDING_GREEN, plotRight);
    const bracketY = plotTop - 16;
    svg.append('path')
      .attr('d', `M${bandX(n - 1)},${plotTop} V${bracketY} H${plotRight} V${plotTop}`)
      .attr('fill', 'none').attr('stroke', '#333').attr('stroke-width', 1.5);

    // ── Retirement marker at the column boundary nearest the current year ────
    const dated = pts.filter(p => p.vintage !== 1933);
    let retireIdx = -1;
    for (let i = 0; i < dated.length; i++) {
      if (dated[i].vintage > this.asOfYear) { retireIdx = i; }
    }
    if (retireIdx >= 0 && retireIdx < n - 1) {
      const rx = bandX(retireIdx + 1);
      const p = pill('Retirement', this.ORANGE, Math.min(rx - 10, landing.left - 14));
      retireLayer.append('path')
        .attr('d', `M${p.right},${pillY + pillH / 2} H${rx} V${plotBottom}`)
        .attr('fill', 'none').attr('stroke', this.ORANGE).attr('stroke-width', 2.5);
    }

    // ── Guide table, columns aligned to the chart columns ────────────────────
    const tableTop = plotBottom + 21;
    const tableInset = 24; // keep the label column inside the rounded card border
    const fo = svg.append('foreignObject')
      .attr('x', tableInset).attr('y', tableTop)
      .attr('width', plotRight - tableInset).attr('height', 10); // grown to fit after measuring
    const foDiv = fo.append('xhtml:div')
      .attr('xmlns', 'http://www.w3.org/1999/xhtml')
      .style('overflow', 'visible')
      .html(this.tableHtml(margin.left - tableInset, bandW));
    const tableH = Math.ceil((foDiv.node() as HTMLElement)?.getBoundingClientRect().height ?? 180);
    fo.attr('height', tableH);

    // ── Disclosure ───────────────────────────────────────────────────────────
    const discTop = tableTop + tableH + 14;
    const discFo = svg.append('foreignObject')
      .attr('x', 24).attr('y', discTop)
      .attr('width', width - 48).attr('height', 10);
    const discDiv = discFo.append('xhtml:div')
      .attr('xmlns', 'http://www.w3.org/1999/xhtml')
      .style('overflow', 'visible')
      .html(`<p style="margin:0; font-family:${this.FONT}; font-size:13.5px; line-height:1.45; color:#333; text-align:justify;">${this.DISCLOSURE}</p>`);
    const discH = Math.ceil((discDiv.node() as HTMLElement)?.getBoundingClientRect().height ?? 70);
    discFo.attr('height', discH);

    // ── Card frame + final size ──────────────────────────────────────────────
    const totalH = discTop + discH + 22;
    svg.attr('viewBox', `0 0 ${width} ${totalH}`).style('height', 'auto');
    svg.append('rect')
      .attr('x', 4).attr('y', 4)
      .attr('width', width - 8).attr('height', totalH - 8).attr('rx', 18)
      .attr('fill', '#fff').attr('stroke', '#d5d5d5').attr('stroke-width', 1.5)
      .lower();

    this.attachHover(host, svg, { plotLeft, plotTop, plotBottom, plotRight, bandW, n });
  }

  private attachHover(
    host: HTMLElement,
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    geo: { plotLeft: number; plotTop: number; plotBottom: number; plotRight: number; bandW: number; n: number }
  ): void {
    const [stocks, bonds] = this.series;
    const hoverBand = svg.append('rect')
      .attr('class', 'gp-hover')
      .attr('y', geo.plotTop).attr('height', geo.plotBottom - geo.plotTop)
      .attr('width', geo.bandW)
      .attr('fill', '#fff').attr('fill-opacity', 0.22)
      .style('pointer-events', 'none')
      .style('display', 'none');
    const tooltip = d3.select(host).append('div').attr('class', 'gp-tooltip').style('display', 'none');

    // Chart column ↔ table column: hovering either highlights both
    const HIGHLIGHT_BG = '#d3e5f1';
    const baseBg = (i: number) => i === this.youCol ? this.YOU_CELL_BG : '#ececec';
    const cellsByCol: HTMLElement[][] = [];
    for (let i = 0; i < geo.n; i++) {
      cellsByCol.push(Array.from(host.querySelectorAll<HTMLElement>(`.gp-td-${i}`)));
    }
    let activeCol: number | null = null;
    const setActive = (i: number | null) => {
      if (i === activeCol) { return; }
      if (activeCol !== null) { cellsByCol[activeCol].forEach(el => el.style.background = baseBg(activeCol!)); }
      activeCol = i;
      if (i === null) {
        hoverBand.style('display', 'none');
      } else {
        cellsByCol[i].forEach(el => el.style.background = HIGHLIGHT_BG);
        hoverBand.attr('x', geo.plotLeft + i * geo.bandW).style('display', null);
      }
    };
    cellsByCol.forEach((cells, i) => cells.forEach(el => {
      el.addEventListener('mouseenter', () => setActive(i));
      el.addEventListener('mouseleave', () => setActive(null));
    }));

    svg.append('rect')
      .attr('class', 'gp-hover')
      .attr('x', geo.plotLeft).attr('y', geo.plotTop)
      .attr('width', geo.plotRight - geo.plotLeft).attr('height', geo.plotBottom - geo.plotTop)
      .attr('fill', 'transparent')
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event, svg.node());
        const i = Math.min(geo.n - 1, Math.max(0, Math.floor((mx - geo.plotLeft) / geo.bandW)));
        const p = this.view[i];
        const l = this.leftEquity(i), r = this.rightEquity(i);
        const range = (a: number, b: number) =>
          Math.round(a) === Math.round(b) ? `${Math.round(a)}%` : `${Math.round(a)}% → ${Math.round(b)}%`;
        setActive(i);
        tooltip
          .style('display', null)
          .html(`<strong>${p.label === 'Income' ? 'Retirement Portfolio' : p.label}</strong>` +
                `<div><span class="dot" style="background:${stocks.color}"></span>Stocks: ${range(l, r)}</div>` +
                `<div><span class="dot" style="background:${bonds.color}"></span>Bonds: ${range(100 - l, 100 - r)}</div>`);
        const hostRect = host.getBoundingClientRect();
        tooltip
          .style('left', `${Math.min(event.clientX - hostRect.left + 14, hostRect.width - 200)}px`)
          .style('top', `${event.clientY - hostRect.top + 14}px`);
      })
      .on('mouseleave', () => {
        setActive(null);
        tooltip.style('display', 'none');
      });
  }

  /** QDIA-notice style guide table; column widths mirror the chart columns. */
  private tableHtml(labelW: number, bandW: number): string {
    const cols = this.guideCols();
    const cell = 'padding:4px; text-align:center; background:#ececec; border-right:1px solid #bdbdbd; border-bottom:2px solid #fff; line-height:1.15;';
    const label = 'padding:4px 12px 4px 4px; text-align:right; background:#e2e2e2; border-bottom:2px solid #fff; line-height:1.15;';
    const split = 'border-left:2px solid #555;';
    const last = cols.length - 1;

    const rows: Array<{ label: string; get: (c: GuideCol) => string }> = [
      { label: 'Target Date',        get: c => c.targetDate },
      { label: 'Suitable Age',       get: c => c.age },
      { label: 'Years to<br>Retirement', get: c => c.years },
      {
        label: 'Allocation<br><span style="font-size:11px; font-weight:400;">Stock/Bond<br>Funds (%)<br>(rounded)</span>',
        get: c => c.mix
      },
    ];

    return `<table style="border-collapse:collapse; width:100%; table-layout:fixed; font-family:${this.FONT}; font-size:14px; color:#222;">` +
      `<colgroup><col style="width:${labelW}px">` +
      cols.map(() => `<col style="width:${bandW}px">`).join('') +
      `</colgroup><tbody>` +
      rows.map(r =>
        `<tr><td style="${label}">${r.label}</td>` +
        cols.map((c, i) =>
          `<td class="gp-td-${i}" style="${cell}${i === last ? split : ''}` +
          `${i === this.youCol ? ` background:${this.YOU_CELL_BG};` : ''}">${r.get(c)}</td>`).join('') +
        `</tr>`).join('') +
      `</tbody></table>`;
  }

  /** One guide-table column per visible vintage, derived from the live equity data. */
  private guideCols(): GuideCol[] {
    const pts = this.view;
    const year = this.asOfYear;
    const R = this.retireAge();
    const mix = (e: number) => `${Math.round(e)}/${Math.round(100 - e)}`;

    return pts.map((p, i) => {
      const income = p.vintage === 1933;
      const d = p.vintage - year;   // years until this vintage's target date
      const ageStart = R - d;       // age today of an investor suited to this vintage
      let targetDate: string;
      let age: string;
      let years: string;

      if (income) {
        const prev = pts[i - 1];
        const prevEnd = prev && prev.vintage !== 1933 ? R - (prev.vintage - year) + 4 : ageStart;
        targetDate = '<span style="font-size:11.5px; line-height:1.15;">Retirement<br>Portfolio</span>';
        age = `&gt;${prevEnd + 1}`;
        years = 'Retired';
      } else {
        targetDate = String(p.vintage);
        if (i === 0) {
          const next = pts[1] && pts[1].vintage !== 1933 ? pts[1] : null;
          age = `&lt;${next ? R - (next.vintage - year) : ageStart + 5}`;
          years = `${d - 4} years<br>or more`;
        } else {
          age = `${ageStart}-${ageStart + 4}`;
          years = d <= -5 ? 'Retired'
                : d <= 0  ? 'Newly<br>Retired'
                : d <= 5  ? `${d + 1} years<br>or less`
                : `${d - 4}-${d + 1}<br>years`;
        }
      }

      const l = this.leftEquity(i), r = this.rightEquity(i);
      return {
        targetDate,
        age,
        years,
        mix: income ? mix(l) : `${mix(l)}<br>to<br>${mix(r)}`,
      };
    });
  }

  /** Saves the chart (title, table and disclosure included) as a standalone .svg file. */
  downloadSvg(): void {
    const svgEl = this.chartEl.nativeElement.querySelector('svg');
    if (!svgEl) { return; }
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll('.gp-hover').forEach(n => n.remove());
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(svgEl.viewBox.baseVal.width));
    clone.setAttribute('height', String(svgEl.viewBox.baseVal.height));
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#fff');
    clone.insertBefore(bg, clone.firstChild);
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ifc-target-date-glide-path.svg';
    a.click();
    URL.revokeObjectURL(url);
  }

  private fmt(d: Date): string {
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

}
